import React, { useState, useEffect } from "react";
import { Modal, Steps, Button, Typography, Space, message, Input, QRCode } from "antd";
import {
  SafetyOutlined,
  QrcodeOutlined,
  CheckCircleOutlined,
  CopyOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

/**
 * TwoFactorSetup Component
 * Guides users through setting up 2FA Authenticator App
 * 
 * @param {boolean} visible - Controls modal visibility
 * @param {string} qrCodeUrl - QR Code URL/data for authenticator app
 * @param {string} secretKey - Manual entry secret key
 * @param {function} onVerify - Callback to verify the setup code
 * @param {function} onCancel - Callback when user cancels
 * @param {boolean} loading - Shows loading state
 */
const TwoFactorSetup = ({
  visible,
  qrCodeUrl,
  secretKey,
  onVerify,
  onCancel,
  loading = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [verificationCode, setVerificationCode] = useState("");

  useEffect(() => {
    if (!visible) {
      setCurrentStep(0);
      setVerificationCode("");
    }
  }, [visible]);

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secretKey);
    message.success("Secret key copied to clipboard");
  };

  const handleVerify = () => {
    if (verificationCode.length !== 6) {
      message.error("Please enter a valid 6-digit code");
      return;
    }
    onVerify(verificationCode);
  };

  const steps = [
    {
      title: "Download App",
      icon: <SafetyOutlined />,
      content: (
        <div style={{ padding: "24px 0" }}>
          <div style={{ textAlign: "center", marginBottom: "24px" }}>
            <Title level={4} style={{ marginBottom: "8px" }}>
              Install an Authenticator App
            </Title>
            <Paragraph type="secondary" style={{ fontSize: "14px", marginBottom: 0 }}>
              Choose and install one of these trusted authenticator apps on your mobile device
            </Paragraph>
          </div>

          <div style={{ maxWidth: "500px", margin: "0 auto" }}>
            {/* Google Authenticator */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                marginBottom: "12px",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                transition: "all 0.3s",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#1890ff";
                e.currentTarget.style.backgroundColor = "#f0f5ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e8e8e8";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  backgroundColor: "#4285F4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "16px",
                  fontSize: "24px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                G
              </div>
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: "16px", display: "block" }}>
                  Google Authenticator
                </Text>
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  Free • iOS & Android
                </Text>
              </div>
              <SafetyOutlined style={{ fontSize: "20px", color: "#52c41a" }} />
            </div>

            {/* Microsoft Authenticator */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                marginBottom: "12px",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                transition: "all 0.3s",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#1890ff";
                e.currentTarget.style.backgroundColor = "#f0f5ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e8e8e8";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #00A4EF 0%, #0078D4 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "16px",
                  fontSize: "24px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                M
              </div>
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: "16px", display: "block" }}>
                  Microsoft Authenticator
                </Text>
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  Free • iOS & Android
                </Text>
              </div>
              <SafetyOutlined style={{ fontSize: "20px", color: "#52c41a" }} />
            </div>

            {/* Authy */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: "16px",
                border: "1px solid #e8e8e8",
                borderRadius: "8px",
                backgroundColor: "#fafafa",
                transition: "all 0.3s",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#1890ff";
                e.currentTarget.style.backgroundColor = "#f0f5ff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e8e8e8";
                e.currentTarget.style.backgroundColor = "#fafafa";
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #EC1C24 0%, #C1272D 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginRight: "16px",
                  fontSize: "24px",
                  color: "white",
                  fontWeight: "bold",
                }}
              >
                A
              </div>
              <div style={{ flex: 1 }}>
                <Text strong style={{ fontSize: "16px", display: "block" }}>
                  Authy
                </Text>
                <Text type="secondary" style={{ fontSize: "13px" }}>
                  Free • iOS, Android & Desktop
                </Text>
              </div>
              <SafetyOutlined style={{ fontSize: "20px", color: "#52c41a" }} />
            </div>
          </div>

          <div
            style={{
              marginTop: "24px",
              padding: "16px",
              backgroundColor: "#e6f7ff",
              borderRadius: "8px",
              borderLeft: "4px solid #1890ff",
            }}
          >
            <Text style={{ fontSize: "13px", color: "#096dd9" }}>
              <SafetyOutlined style={{ marginRight: "8px" }} />
              <strong>Tip:</strong> These apps work offline and provide an extra layer of security for your account.
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Scan QR Code",
      icon: <QrcodeOutlined />,
      content: (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Title level={4}>Scan the QR Code</Title>
          <Paragraph>
            Open your authenticator app and scan this QR code:
          </Paragraph>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "20px 0",
            }}
          >
            {qrCodeUrl ? (
              <QRCode value={qrCodeUrl} size={200} />
            ) : (
              <div
                style={{
                  width: 200,
                  height: 200,
                  border: "1px dashed #d9d9d9",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Text type="secondary">QR Code will appear here</Text>
              </div>
            )}
          </div>
          <Paragraph type="secondary">
            Can't scan? Enter this code manually:
          </Paragraph>
          <Space>
            <Input
              value={secretKey}
              readOnly
              style={{ fontFamily: "monospace", width: 250 }}
            />
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopySecret}
              type="default"
            >
              Copy
            </Button>
          </Space>
        </div>
      ),
    },
    {
      title: "Verify",
      icon: <CheckCircleOutlined />,
      content: (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Title level={4}>Enter Verification Code</Title>
          <Paragraph>
            Enter the 6-digit code from your authenticator app:
          </Paragraph>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              margin: "30px 0",
            }}
          >
            <Input
              placeholder="000000"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, "");
                if (value.length <= 6) {
                  setVerificationCode(value);
                }
              }}
              maxLength={6}
              style={{
                width: 200,
                fontSize: 24,
                textAlign: "center",
                letterSpacing: "8px",
                fontFamily: "monospace",
              }}
            />
          </div>
          <Paragraph type="secondary">
            The code changes every 30 seconds
          </Paragraph>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title={
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Set Up Two-Factor Authentication
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Secure your account with an authenticator app
          </Text>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        currentStep > 0 && (
          <Button
            key="back"
            onClick={() => setCurrentStep(currentStep - 1)}
            disabled={loading}
          >
            Back
          </Button>
        ),
        currentStep < steps.length - 1 ? (
          <Button
            key="next"
            type="primary"
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={loading}
          >
            Next
          </Button>
        ) : (
          <Button
            key="verify"
            type="primary"
            onClick={handleVerify}
            loading={loading}
            disabled={verificationCode.length !== 6}
          >
            Verify & Complete Setup
          </Button>
        ),
      ]}
      width={600}
      centered
      closable={!loading}
    >
      <div style={{ padding: "20px 0" }}>
        <Steps current={currentStep} style={{ marginBottom: 30 }}>
          {steps.map((step) => (
            <Step key={step.title} title={step.title} icon={step.icon} />
          ))}
        </Steps>
        <div>{steps[currentStep].content}</div>
      </div>
    </Modal>
  );
};

export default TwoFactorSetup;
